// Route history — a wide drawer opened from a pin card's "Show route
// history": one person's recorded positions over a preset range (Today /
// Yesterday / Last 7 days / Last 30 days / Custom From–To), from
// POST live-tracking/linked-user-routes (one route per clock in the range).
// Left, the route on a Google map (solid line per clock, a dot per point, the
// latest point emphasised, a pill where a gap starts); right, the timeline
// (day dividers, point times, gap cards). A "gap" is a silence between two
// points of the SAME clock longer than twice the capture interval from the
// attendance settings. On Today the route of the person's open clock is
// merged with the live socket trail, so new fixes land without a refetch;
// the live pill says when the last one arrived. Footer: the retention period
// from the settings.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GoogleMap,
  MarkerF,
  OverlayView,
  OverlayViewF,
  PolylineF,
  useJsApiLoader,
} from '@react-google-maps/api';
import {
  Avatar,
  Button,
  Chip,
  Drawer,
  Field,
  Icon,
  Input,
  SegmentedControl,
  type ChipVariant,
} from '@/components/common';
import { ClockInState } from '@/api/endpoints/attendanceApi';
import {
  RouteDateFilter,
  mergeTrail,
  useGetLinkedUserRoutesQuery,
  type LinkedUser,
  type LinkedUserRoutesArgs,
  type LiveLocation,
  type LiveUser,
  type UserRoute,
} from '@/api/endpoints/liveTrackingApi';
import {
  DEFAULT_ATTENDANCE_SETTINGS,
  useGetAttendanceSettingsQuery,
} from '@/api/endpoints/generalSettingsApi';
import { MAP_API_KEY } from '@/config/config';
import toneOf from '@/utils/avatarTone';
import initialsOf from '@/utils/initials';
import i18n from '@/i18n/config';
import { cssColor } from '../utils/cssColor';
import { fmtAgo, fmtClock } from '../utils/lastSeen';
import {
  buildTimeline,
  countGaps,
  countPoints,
  customRangeIso,
  fmtGapDuration,
  fmtPresetRange,
  gapThresholdMs,
  type TimelineEntry,
} from '../utils/routeHistory';

const PRESETS = [
  { value: RouteDateFilter.Today, labelKey: 'attendance_range_today' },
  { value: RouteDateFilter.Yesterday, labelKey: 'attendance_range_yesterday' },
  { value: RouteDateFilter.Last7Days, labelKey: 'attendance_range_7d' },
  { value: RouteDateFilter.Last30Days, labelKey: 'attendance_range_30d' },
  { value: RouteDateFilter.Custom, labelKey: 'attendance_range_custom' },
] as const;

const SINGLE_POINT_ZOOM = 15;
const FIT_PADDING = 48;

const latLng = (p: LiveLocation) => ({ lat: p.latitude, lng: p.longitude });

/** The route map: one solid line per clock, a dot per point, the latest point
 *  larger and dark, and a pill on the point that starts each gap. Shares the
 *  live-locations loader id, so the Maps script is only ever loaded once. */
const RouteMap = ({ routes, entries }: { routes: UserRoute[]; entries: TimelineEntry[] }) => {
  const { t } = useTranslation();
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: MAP_API_KEY,
    language: i18n.language,
  });
  const colors = useMemo(
    () => ({ line: cssColor('--brand'), latest: cssColor('--text'), ring: cssColor('--surface') }),
    [],
  );
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const points = useMemo(() => routes.flatMap((r) => r.points), [routes]);
  const pointsKey = points.map((p) => p.trackedAt).join(',');

  // Fit around every point whenever the set changes (a single point: pan + zoom).
  useEffect(() => {
    if (!map || points.length === 0) return;
    if (points.length === 1) {
      map.panTo(latLng(points[0]));
      map.setZoom(SINGLE_POINT_ZOOM);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    points.forEach((p) => bounds.extend(latLng(p)));
    map.fitBounds(bounds, FIT_PADDING);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, pointsKey]);

  if (!MAP_API_KEY || loadError) {
    return (
      <div className='route-history__map route-history__map--placeholder'>
        <Icon name='map-pin' size={24} aria-hidden='true' />
        {loadError ? t('attendance_live_map_error') : null}
      </div>
    );
  }
  if (!isLoaded) {
    return (
      <div className='route-history__map route-history__map--placeholder' aria-hidden='true' />
    );
  }

  const latest = entries.find((e) => e.kind === 'point' && e.isLatest);
  const gaps = entries.filter(
    (e): e is Extract<TimelineEntry, { kind: 'gap' }> => e.kind === 'gap',
  );

  return (
    <div className='route-history__map'>
      <GoogleMap
        mapContainerClassName='route-history__map-canvas'
        onLoad={setMap}
        onUnmount={() => setMap(null)}
        options={{
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          zoomControl: true,
          clickableIcons: false,
          cameraControl: false,
        }}
      >
        {routes
          .filter((r) => r.points.length >= 2)
          .map((r) => (
            <PolylineF
              key={`line-${r.attendance.attendanceId}`}
              path={r.points.map(latLng)}
              options={{
                clickable: false,
                strokeColor: colors.line,
                strokeOpacity: 0.9,
                strokeWeight: 4,
              }}
            />
          ))}
        {points.map((p) => {
          const isLatest = latest?.kind === 'point' && latest.point === p;
          return (
            <MarkerF
              key={`pt-${p.attendanceId}-${p.trackedAt}`}
              position={latLng(p)}
              clickable={false}
              zIndex={isLatest ? 2 : 1}
              icon={{
                path: google.maps.SymbolPath.CIRCLE,
                scale: isLatest ? 7 : 4,
                fillColor: isLatest ? colors.latest : colors.line,
                fillOpacity: 1,
                strokeColor: colors.ring,
                strokeWeight: 2,
              }}
            />
          );
        })}
        {gaps.map((g) => (
          <OverlayViewF
            key={`gap-${g.from.trackedAt}`}
            position={latLng(g.from)}
            mapPaneName={OverlayView.OVERLAY_LAYER}
            getPixelPositionOffset={() => ({ x: 0, y: 0 })}
          >
            <span className='route-history__gap-pill'>
              {t('attendance_route_gap_short', { duration: fmtGapDuration(g.ms, t) })}
            </span>
          </OverlayViewF>
        ))}
      </GoogleMap>
    </div>
  );
};

/** Header chip: the person's state in the pin-card vocabulary. */
const statusOf = (user: LiveUser): { variant: ChipVariant; labelKey: string } => {
  if (!user.online) return { variant: 'neutral', labelKey: 'attendance_live_offline' };
  if (user.state === ClockInState.Paused)
    return { variant: 'warning', labelKey: 'attendance_status_paused' };
  return { variant: 'success', labelKey: 'attendance_pin_checked_in' };
};

interface RouteHistoryDrawerProps {
  open: boolean;
  onClose: () => void;
  /** The person, as the live card knows them (name, state, last ping). */
  user: LiveUser;
  /** The same person's raw live record — its trail is patched by the
   *  socket, so Today's open clock stays current without a refetch. */
  liveUser?: LinkedUser;
  /** The socket is connected — the live pill needs it. */
  isLive: boolean;
}

const RouteHistoryDrawer = ({ open, onClose, user, liveUser, isLive }: RouteHistoryDrawerProps) => {
  const { t } = useTranslation();
  const [preset, setPreset] = useState<RouteDateFilter>(RouteDateFilter.Today);
  const [fromYmd, setFromYmd] = useState('');
  const [toYmd, setToYmd] = useState('');

  // A fresh open (or another person) starts on Today.
  useEffect(() => {
    if (open) {
      setPreset(RouteDateFilter.Today);
      setFromYmd('');
      setToYmd('');
    }
  }, [open, user.companyUserId]);

  const custom = customRangeIso(fromYmd, toYmd);
  const customInvalid = preset === RouteDateFilter.Custom && !!fromYmd && !!toYmd && !custom;
  const args: LinkedUserRoutesArgs | null =
    preset === RouteDateFilter.Custom
      ? custom && { userId: user.companyUserId, dateFilter: preset, ...custom }
      : { userId: user.companyUserId, dateFilter: preset };

  const { data, isLoading, isFetching, isError, refetch, fulfilledTimeStamp } =
    useGetLinkedUserRoutesQuery(args ?? { userId: user.companyUserId, dateFilter: preset }, {
      skip: !open || !args,
      refetchOnMountOrArgChange: true,
    });

  const { data: settings } = useGetAttendanceSettingsQuery();
  const thresholdMs = gapThresholdMs((settings ?? DEFAULT_ATTENDANCE_SETTINGS).trackLocationPeriod);

  // Today: the open clock's route takes the socket-patched trail (or appears
  // from it when the clock started after the fetch).
  const routes = useMemo<UserRoute[]>(() => {
    const fetched = data?.routes ?? [];
    const live = liveUser?.attendance;
    if (preset !== RouteDateFilter.Today || !live || live.state === ClockInState.Ended)
      return fetched;
    const idx = fetched.findIndex((r) => r.attendance.attendanceId === live.attendanceId);
    if (idx === -1)
      return liveUser.trackLocations.length
        ? [...fetched, { attendance: live, points: liveUser.trackLocations }]
        : fetched;
    return fetched.map((r, i) =>
      i === idx ? { ...r, points: mergeTrail(r.points, liveUser.trackLocations) } : r,
    );
  }, [data, liveUser, preset]);

  const entries = useMemo(() => buildTimeline(routes, thresholdMs), [routes, thresholdMs]);
  const points = countPoints(routes);
  const gaps = countGaps(entries);
  const status = statusOf(user);
  const readAt = fulfilledTimeStamp ? fmtClock(new Date(fulfilledTimeStamp).toISOString()) : null;
  const showLive = isLive && user.online && preset === RouteDateFilter.Today && !!user.lastPingAt;
  const rangeLabel = fmtPresetRange(preset, new Date(), i18n.language);
  const dayLabel = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
    }).format(new Date(iso));

  return (
    <Drawer
      open={open}
      onClose={onClose}
      closeOnOverlay
      className='route-history'
      header={
        <div className='route-history__header'>
          <Avatar initials={initialsOf(user.fullName)} size={44} tone={toneOf(user.fullName)} />
          <span className='route-history__name'>{user.fullName}</span>
          <Chip variant={status.variant} iconStart='log-in'>
            {t(status.labelKey)}
          </Chip>
        </div>
      }
    >
      <section className='route-history__card'>
        <header className='route-history__head'>
          <div className='route-history__heading'>
            <h3 className='route-history__title'>
              <Icon name='route' size={20} aria-hidden='true' />
              {t('attendance_route_title')}
            </h3>
            <span className='route-history__meta'>
              {[
                t('attendance_route_points', { count: points }),
                t('attendance_route_gaps', { count: gaps }),
                readAt && t('attendance_route_read', { time: readAt }),
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </div>
          {showLive && user.lastPingAt && (
            <span className='route-history__live' role='status'>
              {t('attendance_route_live', { ago: fmtAgo(user.lastPingAt, t) })}
            </span>
          )}
          <Button
            variant='secondary'
            iconStart='refresh-cw'
            loading={isFetching}
            disabled={!args}
            onClick={() => void refetch()}
          >
            {t('attendance_route_refresh')}
          </Button>
        </header>

        <div className='route-history__presets'>
          <SegmentedControl
            aria-label={t('attendance_filter_date')}
            options={PRESETS.map((p) => ({ value: p.value, label: t(p.labelKey) }))}
            value={preset}
            onChange={(v) => setPreset(v as RouteDateFilter)}
          />
          {preset === RouteDateFilter.Custom ? (
            <div className='route-history__custom'>
              <Field label={t('attendance_route_from')} htmlFor='route-history-from'>
                <Input
                  id='route-history-from'
                  type='date'
                  size='sm'
                  value={fromYmd}
                  max={toYmd || undefined}
                  onChange={(e) => setFromYmd(e.target.value)}
                />
              </Field>
              <Field
                label={t('attendance_route_to')}
                htmlFor='route-history-to'
                error={customInvalid ? t('attendance_route_range_invalid') : undefined}
              >
                <Input
                  id='route-history-to'
                  type='date'
                  size='sm'
                  value={toYmd}
                  min={fromYmd || undefined}
                  error={customInvalid}
                  onChange={(e) => setToYmd(e.target.value)}
                />
              </Field>
            </div>
          ) : (
            <span className='route-history__range'>{rangeLabel}</span>
          )}
        </div>

        {isError ? (
          <div className='route-history__state'>
            <Icon name='circle-alert' size={24} aria-hidden='true' />
            {t('attendance_route_error')}
          </div>
        ) : isLoading ? (
          <div className='route-history__state'>{t('text_loading')}</div>
        ) : points === 0 ? (
          <div className='route-history__state'>
            <Icon name='map-pin' size={24} aria-hidden='true' />
            {args ? t('attendance_route_empty') : t('attendance_route_pick_range')}
          </div>
        ) : (
          <div className='route-history__split'>
            <RouteMap routes={routes} entries={entries} />
            <ol className='route-history__timeline'>
              {entries.map((entry) => {
                if (entry.kind === 'day')
                  return (
                    <li key={`day-${entry.at}`} className='route-history__day'>
                      {dayLabel(entry.at)}
                    </li>
                  );
                if (entry.kind === 'gap')
                  return (
                    <li key={`gap-${entry.from.trackedAt}`} className='route-history__gap'>
                      <Icon name='eye-off' size={16} aria-hidden='true' />
                      {t('attendance_route_gap', { duration: fmtGapDuration(entry.ms, t) })}
                    </li>
                  );
                return (
                  <li
                    key={`pt-${entry.point.attendanceId}-${entry.point.trackedAt}`}
                    className={[
                      'route-history__entry',
                      entry.isLatest && 'route-history__entry--latest',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <span className='route-history__entry-dot' aria-hidden='true' />
                    {fmtClock(entry.point.trackedAt)}
                  </li>
                );
              })}
            </ol>
          </div>
        )}

        {settings && (
          <footer className='route-history__footer'>
            <Icon name='history' size={16} aria-hidden='true' />
            {t('attendance_route_retention', { days: settings.retentionPeriod })}
          </footer>
        )}
      </section>
    </Drawer>
  );
};

export default RouteHistoryDrawer;
